import dayjs from "dayjs";
import {
  buildSportsbookAnalyticsFilterParams,
  type SportsbookAnalyticsHttpParams,
} from "./sportsbookAnalyticsFilterParams";

import type {
  ReportingSummaryRow,
  SportsbookAnalyticsSummaryHttpBody,
  SportsbookAnalyticsSummaryItemDto,
  SportsbookAnalyticsSummaryQueryParams,
  SportsbookAnalyticsSummaryResult,
  SportsbookSummaryFilterMetaDto,
} from "./sportsbookAnalyticsSummaryTypes";

const MAX_RANGE_DAYS = 100;

export function inclusiveDayCount(start: string, end: string): number {
  const s = dayjs(start, "YYYY-MM-DD", true).startOf("day");
  const e = dayjs(end, "YYYY-MM-DD", true).startOf("day");
  if (!s.isValid() || !e.isValid()) return 0;
  return e.diff(s, "day") + 1;
}

/** Clamp end date so inclusive span does not exceed `maxDays` (API limit). */
export function clampEndDateToMaxRange(
  startDate: string,
  endDate: string,
  maxDays = MAX_RANGE_DAYS,
): { start_date: string; end_date: string; wasClamped: boolean } {
  const s = dayjs(startDate, "YYYY-MM-DD", true).startOf("day");
  const e = dayjs(endDate, "YYYY-MM-DD", true).startOf("day");
  if (!s.isValid() || !e.isValid()) {
    return { start_date: startDate, end_date: endDate, wasClamped: false };
  }
  const days = e.diff(s, "day") + 1;
  if (days <= maxDays) {
    return {
      start_date: s.format("YYYY-MM-DD"),
      end_date: e.format("YYYY-MM-DD"),
      wasClamped: false,
    };
  }
  const clampedEnd = s.add(maxDays - 1, "day");
  return {
    start_date: s.format("YYYY-MM-DD"),
    end_date: clampedEnd.format("YYYY-MM-DD"),
    wasClamped: true,
  };
}

export function mapAnalyticsSummaryItemToRow(
  dto: SportsbookAnalyticsSummaryItemDto,
): ReportingSummaryRow {
  return {
    key: dto.date,
    date: dayjs(dto.date, "YYYY-MM-DD", true),
    acceptedBets: dto.accepted_bets,
    acceptedTurnover: dto.accepted_turnover,
    avgStake: dto.avg_stake,
    pl: dto.pnl,
    margin: dto.margin,
  };
}

export function mapAnalyticsSummaryItemsToRows(
  items: SportsbookAnalyticsSummaryItemDto[],
): ReportingSummaryRow[] {
  return items.map(mapAnalyticsSummaryItemToRow);
}

function coerceCount(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return fallback;
}

/** Parses the paginated summary HTTP envelope (items, total, page, size, pages, meta.filters). */
export function parseSportsbookAnalyticsSummaryResponse(
  body: unknown,
): Omit<SportsbookAnalyticsSummaryResult, "wasRangeClamped"> {
  const b = body as SportsbookAnalyticsSummaryHttpBody | null | undefined;
  const items = Array.isArray(b?.data?.items) ? b!.data.items : [];
  const totalCount = coerceCount(b?.data?.total, items.length);
  const page = coerceCount(b?.data?.page, 1);
  const size = coerceCount(b?.data?.size, items.length);
  const pages = coerceCount(
    b?.data?.pages,
    size > 0 ? Math.max(1, Math.ceil(totalCount / size)) : 1,
  );
  const filters: SportsbookSummaryFilterMetaDto[] = Array.isArray(
    b?.meta?.filters,
  )
    ? (b!.meta!.filters as SportsbookSummaryFilterMetaDto[])
    : [];
  return { items, totalCount, page, size, pages, filters };
}

/**
 * Builds the axios `params` object for the paginated summary request.
 *
 * - Clamps the date range so summary and KPIs stay aligned on the same window.
 * - Keeps money type/status repeatable; other filter groups remain comma-joined.
 * - Omits empty / undefined filter arrays so the backend applies its own defaults.
 *
 * Token-translation note: we pass through whatever tokens the UI holds (typically the
 * `selected_values` surfaced by `meta.filters`). If the backend expects canonical
 * tokens (e.g. `rebet_cash` instead of `cash`), translation lives server-side.
 */
export function buildSportsbookSummaryParams(
  args: SportsbookAnalyticsSummaryQueryParams,
): { params: SportsbookAnalyticsHttpParams; wasClamped: boolean } {
  const { start_date, end_date, wasClamped } = clampEndDateToMaxRange(
    args.start_date,
    args.end_date,
  );

  const params: SportsbookAnalyticsHttpParams = {
    page: args.page,
    size: args.size,
    start_date,
    end_date,
    ...buildSportsbookAnalyticsFilterParams(args),
  };

  return { params, wasClamped };
}
